import React, { useState } from 'react';
import { Calendar as CalendarIcon, List, Grid, Filter, Plus } from 'lucide-react';
import CalendarGrid from './CalendarGrid';
import CalendarList from './CalendarList';
import EventForm from './EventForm';
import EventFilters from './EventFilters';
import { useCalendar } from '../../hooks/useCalendar';
import { CalendarView, EventPriority } from '../../types/calendar';
import { useDeals } from '../../store/DealsContext';

export default function CalendarDashboard() {
  const [view, setView] = useState<CalendarView>('month');
  const [showEventForm, setShowEventForm] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedDeal, setSelectedDeal] = useState<string>('');
  const [selectedPriority, setSelectedPriority] = useState<EventPriority | ''>('');
  
  const {
    events,
    addEvent,
    updateEvent,
    deleteEvent,
    loading
  } = useCalendar();

  const { deals } = useDeals();

  const filteredEvents = events.filter(event => {
    const matchesDeal = selectedDeal ? event.dealId === selectedDeal : true;
    const matchesPriority = selectedPriority ? event.priority === selectedPriority : true;
    return matchesDeal && matchesPriority;
  });

  // Group events by type for summary
  const eventSummary = {
    total: filteredEvents.length,
    upcoming: filteredEvents.filter(e => new Date(e.startTime) > new Date()).length,
    overdue: filteredEvents.filter(e => 
      new Date(e.startTime) < new Date() && 
      !e.completed
    ).length,
    byType: {
      milestone: filteredEvents.filter(e => e.type === 'milestone').length,
      task: filteredEvents.filter(e => e.type === 'task').length,
      meeting: filteredEvents.filter(e => e.type === 'meeting').length,
      regulatory: filteredEvents.filter(e => e.type === 'regulatory').length
    }
  };

  return (
    <div className="h-full">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Calendar</h2>
        <p className="mt-2 text-gray-600">Track all M&A activities and deadlines</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow-sm">
          <h3 className="text-sm font-medium text-gray-600">Total Events</h3>
          <p className="mt-2 text-2xl font-bold text-gray-900">{eventSummary.total}</p>
          <div className="mt-2 text-sm text-gray-500">
            Across {deals.length} active deals
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm">
          <h3 className="text-sm font-medium text-gray-600">Upcoming</h3>
          <p className="mt-2 text-2xl font-bold text-blue-600">{eventSummary.upcoming}</p>
          <div className="mt-2 text-sm text-gray-500">
            Next 30 days
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm">
          <h3 className="text-sm font-medium text-gray-600">Overdue</h3>
          <p className="mt-2 text-2xl font-bold text-red-600">{eventSummary.overdue}</p>
          <div className="mt-2 text-sm text-gray-500">
            Require attention
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm">
          <h3 className="text-sm font-medium text-gray-600">By Type</h3>
          <div className="mt-2 space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Milestones</span>
              <span className="font-medium text-gray-900">{eventSummary.byType.milestone}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Tasks</span>
              <span className="font-medium text-gray-900">{eventSummary.byType.task}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Meetings</span>
              <span className="font-medium text-gray-900">{eventSummary.byType.meeting}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Regulatory</span>
              <span className="font-medium text-gray-900">{eventSummary.byType.regulatory}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-4">
          <div className="flex items-center bg-white rounded-lg shadow-sm p-1">
            <button
              onClick={() => setView('month')}
              className={`px-3 py-1.5 rounded ${
                view === 'month' ? 'bg-blue-100 text-blue-600' : 'text-gray-600'
              }`}
            >
              <CalendarIcon className="w-5 h-5" />
            </button>
            <button
              onClick={() => setView('week')}
              className={`px-3 py-1.5 rounded ${
                view === 'week' ? 'bg-blue-100 text-blue-600' : 'text-gray-600'
              }`}
            >
              <Grid className="w-5 h-5" />
            </button>
            <button
              onClick={() => setView('list')}
              className={`px-3 py-1.5 rounded ${
                view === 'list' ? 'bg-blue-100 text-blue-600' : 'text-gray-600'
              }`}
            >
              <List className="w-5 h-5" />
            </button>
          </div>

          <EventFilters
            selectedDeal={selectedDeal}
            selectedPriority={selectedPriority}
            onDealChange={setSelectedDeal}
            onPriorityChange={setSelectedPriority}
          />
        </div>

        <button
          onClick={() => setShowEventForm(true)}
          className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Event
        </button>
      </div>

      {view === 'list' ? (
        <CalendarList
          events={filteredEvents}
          onEventClick={(event) => {
            setSelectedDate(new Date(event.startTime));
            setShowEventForm(true);
          }}
        />
      ) : (
        <CalendarGrid
          view={view}
          events={filteredEvents}
          selectedDate={selectedDate}
          onDateSelect={setSelectedDate}
          onEventClick={(event) => {
            setSelectedDate(new Date(event.startTime));
            setShowEventForm(true);
          }}
        />
      )}

      {showEventForm && (
        <EventForm
          selectedDate={selectedDate}
          onClose={() => setShowEventForm(false)}
          onSubmit={addEvent}
        />
      )}
    </div>
  );
}